/**
 * PM-5 Step 1 — Upload a workbook (`.xlsx`/`.csv`) → `POST /imports/analyze`
 * (multipart, PM-2). The BE parses the workbook (PM-1d) and returns the
 * parsed sheets + suggested sheet/header + fuzzy mapping + per-row
 * validation, so the FE needs NO xlsx parser. Mirrors
 * `features/ai-findings-import/Step2ModelFiles.tsx` (file drop + analyze).
 */
import { useRef, type ChangeEvent, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FileUpIcon } from "@aeriosiq/ui/icons";
const ACCEPT =
  ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

export interface Step1UploadProps {
  /** The currently-selected file (lifted into the wizard), or null. */
  file: File | null;
  /** Called with the chosen file (fires the analyze upload upstream). */
  onPickFile: (file: File) => void;
  isAnalyzing: boolean;
  error: string | null;
}

export function Step1Upload({
  file,
  onPickFile,
  isAnalyzing,
  error,
}: Step1UploadProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0];
    if (picked) onPickFile(picked);
    // Allow re-picking the same file name.
    e.target.value = "";
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      <Typography variant="body2" color="text.secondary">
        Upload a turbine workbook (<code>.xlsx</code> or <code>.csv</code>). It
        is parsed on the server — you&apos;ll pick the sheet, map columns, and
        edit values in the next steps. Nothing is imported until you confirm.
      </Typography>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleChange}
        style={{ display: "none" }}
        data-testid="import-file-input"
        aria-label="Workbook file"
      />

      <Box>
        <Button
          variant="outlined"
          startIcon={<FileUpIcon fontSize="small" />}
          onClick={() => inputRef.current?.click()}
          disabled={isAnalyzing}
        >
          {file ? "Choose a different file" : "Choose file"}
        </Button>
      </Box>

      {file ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">
            Selected: <strong>{file.name}</strong>
          </Typography>
          {isAnalyzing ? (
            <CircularProgress size={16} aria-label="Analyzing" />
          ) : null}
        </Stack>
      ) : null}

      {isAnalyzing ? (
        <Typography variant="caption" color="text.secondary">
          Parsing and analyzing the workbook…
        </Typography>
      ) : null}

      {error !== null ? (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      ) : null}
    </Stack>
  );
}
